import type { Dispatch, SetStateAction } from "react";
import { useMemo, useRef, useState } from "react";

import type { IconName } from "@similarweb/icons";
import type { ActionButton } from "@similarweb/pro-logic-components";

import { onHookClick } from "app/components/sidebar3/SideBarItems";
import { HOOKS_ICON } from "app/constants/hooks";
import { translate } from "app/filters/i18nFilter";
import { getIsRTCreateCampaignsAvailable } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignsInfraUtils";
import { EPrimarySideBar3StateType } from "app/reducers/_reducers/PrimarySidebar3Reducer";
import { useKeywordGroups } from "app/services/keyword-groups";
import { EHooksVersions } from "app/services/SwHook";
import { allTrackers } from "app/services/track/track";
import { buttonTypes } from "app/UtilitiesAndConstants/Constants/ButtonTypes";

import { ADD_ICON, DELETE_ICON } from "../TableSelectionGroupKeywordConst";

const trackKeywordListAction = () => {
    allTrackers.dispatchTrackEvent("Button", "click", "Keyword List");
};

export interface IUseTableSelectionButtons {
    clearAllSelectedRows: () => void;
    actionButtons: ActionButton[];
    keywordListButtonRef: React.RefObject<HTMLButtonElement | null>;
    searchTrackerButtonRef: React.RefObject<HTMLButtonElement | null>;
    isKeywordListPopoverOpen: boolean;
    isCreateNewListModalOpen: boolean;
    isSearchTrackerPopoverOpen: boolean;
    isKeywordRemovalModalOpen: boolean;
    setIsKeywordListPopoverOpen: (isOpen: boolean) => void;
    setIsCreateNewListModalOpen: (isOpen: boolean) => void;
    setIsSearchTrackerPopoverOpen: (isOpen: boolean) => void;
    setIsKeywordRemovalModalOpen: (isOpen: boolean) => void;
}

export const useTableSelectionButtons = (
    setSelectedRows: Dispatch<SetStateAction<Record<string, unknown>[]>>,
    hasKeywordRemoval?: boolean,
): IUseTableSelectionButtons => {
    const [isKeywordListPopoverOpen, setIsKeywordListPopoverOpen] = useState(false);
    const [isCreateNewListModalOpen, setIsCreateNewListModalOpen] = useState(false);
    const [isSearchTrackerPopoverOpen, setIsSearchTrackerPopoverOpen] = useState(false);
    const [isKeywordRemovalModalOpen, setIsKeywordRemovalModalOpen] = useState(false);
    const userGroups = useKeywordGroups();

    const keywordListButtonRef = useRef<HTMLButtonElement>(null);
    const searchTrackerButtonRef = useRef<HTMLButtonElement>(null);

    const isRTCreateCampaignsAllowed = getIsRTCreateCampaignsAvailable();

    // @ts-ignore FIXME: strict migration - Type '(... | null)[]' is not assignable to type 'ActionButton[]' — locally-built button objects don't structurally match the external ActionButton type (from @similarweb/pro-logic-components) and .filter(Boolean) doesn't narrow out null
    const actionButtons: ActionButton[] = useMemo(() => {
        return [
            hasKeywordRemoval
                ? {
                      type: buttonTypes.FLAT,
                      iconName: DELETE_ICON as IconName,
                      children: translate("table.selection.remove.from.list"),
                      onClick: () => {
                          setIsKeywordRemovalModalOpen(true);
                          allTrackers.dispatchTrackEvent(
                              "Button",
                              "click",
                              "Keyword List Edit/Remove keyword button clicked",
                          );
                      },
                  }
                : null,
            (userGroups.data?.length ?? 0) > 0
                ? {
                      type: buttonTypes.FLAT,
                      iconName: ADD_ICON as IconName,
                      children: translate("table.selection.keyword.list"),
                      onClick: () => {
                          trackKeywordListAction();
                          setIsKeywordListPopoverOpen(true);
                      },
                      alwaysAvailable: false,
                      ref: keywordListButtonRef,
                  }
                : {
                      type: buttonTypes.FLAT,
                      iconName: ADD_ICON as IconName,
                      children: translate("table.selection.create.group"),
                      onClick: () => {
                          trackKeywordListAction();
                          setIsCreateNewListModalOpen(true);
                      },
                      alwaysAvailable: false,
                  },
            {
                type: buttonTypes.FLAT,
                iconName: (isRTCreateCampaignsAllowed ? ADD_ICON : HOOKS_ICON) as IconName,
                children: translate("table.selection.search.tracker"),
                onClick: () => {
                    if (isRTCreateCampaignsAllowed) {
                        setIsSearchTrackerPopoverOpen(true);
                        allTrackers.dispatchTrackEvent(
                            "Button",
                            "click",
                            `Search tracker/${!isKeywordListPopoverOpen ? "Open" : "Close"}`,
                        );
                    } else {
                        onHookClick(
                            "RankTracker_CreateFromKeywordsResearch",
                            EPrimarySideBar3StateType.RankTracker_home,
                            EHooksVersions.V5,
                        );
                    }
                },
                alwaysAvailable: false,
                ref: searchTrackerButtonRef,
            },
        ].filter(Boolean);
    }, [hasKeywordRemoval, isRTCreateCampaignsAllowed, isKeywordListPopoverOpen]);

    return {
        clearAllSelectedRows: () => {
            setSelectedRows([]);
        },
        actionButtons,
        keywordListButtonRef,
        searchTrackerButtonRef,
        isKeywordListPopoverOpen,
        isCreateNewListModalOpen,
        isSearchTrackerPopoverOpen,
        setIsKeywordListPopoverOpen,
        setIsCreateNewListModalOpen,
        setIsSearchTrackerPopoverOpen,
        isKeywordRemovalModalOpen,
        setIsKeywordRemovalModalOpen,
    };
};
